import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { updateJobTask, getJobTask } from 'http/jobsTasks';
import { useRouteMatch, useHistory } from 'react-router-dom';
import NotFound from 'pages/NotFound';
import { routesAliases } from 'router/routesAliases';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import TaskForm from '../Form';
import JobTaskVersionsTable from './JobTaskVersionsTable';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(4, 5),
  },
}));

const Edit = ({ routeProps }) => {
  const routerMatch = useRouteMatch();
  const history = useHistory();
  const { t } = useTranslation();
  const userRole = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = userRole === ROLES.HYPER;
  const { id: jobTaskId } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [initialFormState, setInitialFormState] = useState(null);
  const [isFromSystem, setIsFromSystem] = useState(false);
  const backUrl = routesAliases.jobsTasksList;

  const classes = useStyles();
  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: t(routeProps.title),
      isActive: true,
    },
  ];

  const [submittingState, sendForm] = useAsyncFn(async formData => {
    const updateResult = await updateJobTask(jobTaskId, formData);
    if (!updateResult.error && !updateResult.statusCode) {
      history.push(backUrl);
    }
    return updateResult;
  }, [backUrl, history, jobTaskId]);

  const [fetchJobTaskState, fetchJobTaskData] = useAsyncFn(async id => {
    const jobTask = await getJobTask(id);
    if (!jobTask.error) {
      setInitialFormState({
        name: jobTask.name,
      });
      setIsFromSystem(Boolean(jobTask.isFromSystem));
    } else if (jobTask.statusCode === 404) {
      setNotFoundError(true);
    }
    return jobTask;
  }, []);

  useEffect(() => {
    setNotFoundError(false);
    setInitialFormState(null);
    fetchJobTaskData(jobTaskId);
  }, [jobTaskId]);

  return notFoundError ? (
    <NotFound />
  ) : (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchJobTaskState.loading}
    >
      <TaskForm
        key={jobTaskId}
        onSubmitForm={sendForm}
        submitting={submittingState.loading}
        formType="edit"
        initialValues={initialFormState}
        backUrl={backUrl}
        isReadOnly={isFromSystem && !isHyperAdmin}
      />
      <JobTaskVersionsTable jobTaskId={jobTaskId} isFromSystem={isFromSystem} />
    </PageLayout>
  );
};

Edit.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default Edit;
